'use client'

import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { EditFormActions } from '@/feed/row-chrome'

// ============================================================================
// Artist Edit Form
// ============================================================================

interface ArtistEditFormProps {
  editName: string
  setEditName: (v: string) => void
  editTopics: string
  setEditTopics: (v: string) => void
  editBio: string
  setEditBio: (v: string) => void
  editNotes: string
  setEditNotes: (v: string) => void
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

export function ArtistEditForm({
  editName,
  setEditName,
  editTopics,
  setEditTopics,
  editBio,
  setEditBio,
  editNotes,
  setEditNotes,
  isSaving,
  onCancel,
  onSave,
}: ArtistEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor="artist-edit-name"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Name
        </label>
        <Input
          id="artist-edit-name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="h-8 text-sm"
          placeholder="Artist name"
        />
      </div>

      <div>
        <label
          htmlFor="artist-edit-topics"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Topics (comma-separated)
        </label>
        <Input
          id="artist-edit-topics"
          value={editTopics}
          onChange={(e) => setEditTopics(e.target.value)}
          className="h-8 text-sm"
          placeholder="Rock, Pop, Jazz"
        />
      </div>

      <div>
        <label
          htmlFor="artist-edit-bio"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Bio
        </label>
        <Textarea
          id="artist-edit-bio"
          value={editBio}
          onChange={(e) => setEditBio(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Artist biography..."
        />
      </div>

      <div>
        <label
          htmlFor="artist-edit-notes"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Notes
        </label>
        <Textarea
          id="artist-edit-notes"
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Your personal notes..."
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  )
}

// ============================================================================
// Label Edit Form
// ============================================================================

interface LabelEditFormProps {
  editName: string
  setEditName: (v: string) => void
  editWebsite: string
  setEditWebsite: (v: string) => void
  editProfile: string
  setEditProfile: (v: string) => void
  editNotes: string
  setEditNotes: (v: string) => void
  editTopics: string
  setEditTopics: (v: string) => void
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

export function LabelEditForm({
  editName,
  setEditName,
  editWebsite,
  setEditWebsite,
  editProfile,
  setEditProfile,
  editNotes,
  setEditNotes,
  editTopics,
  setEditTopics,
  isSaving,
  onCancel,
  onSave,
}: LabelEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor="label-edit-name"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Name
        </label>
        <Input
          id="label-edit-name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="h-8 text-sm"
          placeholder="Label name"
        />
      </div>

      <div>
        <label
          htmlFor="label-edit-topics"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Topics (comma-separated)
        </label>
        <Input
          id="label-edit-topics"
          value={editTopics}
          onChange={(e) => setEditTopics(e.target.value)}
          className="h-8 text-sm"
          placeholder="Rock, Pop, Jazz"
        />
      </div>

      <div>
        <label
          htmlFor="label-edit-website"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Website
        </label>
        <Input
          id="label-edit-website"
          value={editWebsite}
          onChange={(e) => setEditWebsite(e.target.value)}
          className="h-8 text-sm"
          placeholder="https://example.com"
        />
      </div>

      <div>
        <label
          htmlFor="label-edit-profile"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Profile
        </label>
        <Textarea
          id="label-edit-profile"
          value={editProfile}
          onChange={(e) => setEditProfile(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Label description..."
        />
      </div>

      <div>
        <label
          htmlFor="label-edit-notes"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Notes
        </label>
        <Textarea
          id="label-edit-notes"
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Your personal notes..."
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  )
}

// ============================================================================
// Release Edit Form
// ============================================================================

interface ReleaseEditFormProps {
  editTitle: string
  setEditTitle: (v: string) => void
  editYear: string
  setEditYear: (v: string) => void
  editTopics: string
  setEditTopics: (v: string) => void
  editNotes: string
  setEditNotes: (v: string) => void
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

export function ReleaseEditForm({
  editTitle,
  setEditTitle,
  editYear,
  setEditYear,
  editTopics,
  setEditTopics,
  editNotes,
  setEditNotes,
  isSaving,
  onCancel,
  onSave,
}: ReleaseEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor="release-edit-title"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Title
        </label>
        <Input
          id="release-edit-title"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder="Release title"
        />
      </div>

      <div>
        <label
          htmlFor="release-edit-year"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Year
        </label>
        <Input
          id="release-edit-year"
          type="number"
          value={editYear}
          onChange={(e) => setEditYear(e.target.value)}
          className="h-8 text-sm"
          placeholder="2024"
        />
      </div>

      <div>
        <label
          htmlFor="release-edit-topics"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Topics (comma-separated)
        </label>
        <Input
          id="release-edit-topics"
          value={editTopics}
          onChange={(e) => setEditTopics(e.target.value)}
          className="h-8 text-sm"
          placeholder="Rock, Pop, Jazz"
        />
      </div>

      <div>
        <label
          htmlFor="release-edit-notes"
          className="text-muted-foreground mb-1 block text-xs"
        >
          Notes
        </label>
        <Textarea
          id="release-edit-notes"
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Your personal notes..."
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  )
}
